import { TableCardSkeleton } from "@/components/loading";
import { DashboardPageHeader, DashboardPageShell } from "@/components/patterns/headers";
import { ReportListItem } from "@/components/reports/report-list-item";
import { LoadingButton } from "@/components/ui/custom/loading-button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { useTRPC } from "@/lib/trpc";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { FileText, Plus } from "lucide-react";

export const Route = createFileRoute("/_dashboard/$brand/reports/")({
  component: RouteComponent,
});

function RouteComponent() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { brand } = Route.useParams();
  const { data: brandObject } = useQuery(trpc.brands.getBrandBySlug.queryOptions({ slug: brand }));
  const brandId = brandObject?.id;
  const brandSlug = brand;
  const { data: reports, isLoading: isLoadingReports } = useQuery(
    trpc.reports.getBrandReports.queryOptions(
      {
        brandId: brandId ?? "",
        skip: 0,
        take: 10,
      },
      { enabled: !!brandId },
    ),
  );

  const { mutate: generateReport, isPending: isGeneratingReport } = useMutation({
    ...trpc.reports.createReport.mutationOptions({}),
    onSuccess: () => {
      if (!brandId) return;
      queryClient.invalidateQueries(
        trpc.reports.getBrandReports.queryOptions({
          brandId,
        }),
      );
    },
  });

  const hasReports = !isLoadingReports && reports?.items && reports.items.length > 0;

  return (
    <DashboardPageShell contentClassName="space-y-6">
      <DashboardPageHeader
        title="Strategy Reports"
        description="Reports are automatically generated every month"
        aside={
          hasReports ? (
            <LoadingButton
              loading={isGeneratingReport}
              disabled={isGeneratingReport || !brandId}
              size="sm"
              onClick={() => {
                if (!brandId) return;
                generateReport({ brandId });
              }}
            >
              <Plus className="mr-1.5 size-3.5" />
              New Report
            </LoadingButton>
          ) : undefined
        }
      />

      {isLoadingReports ? (
        <div className="space-y-3">
          <TableCardSkeleton rows={4} columns={3} />
        </div>
      ) : hasReports ? (
        <div className="flex flex-col gap-2">
          {reports.items.map((report) => (
            <ReportListItem key={report.id} report={report} brandSlug={brandSlug} />
          ))}
        </div>
      ) : (
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <FileText className="h-8 w-8" />
            </EmptyMedia>
            <EmptyTitle>No Reports Yet</EmptyTitle>
            <EmptyDescription>
              You haven&apos;t created any reports yet. Get started by creating your first report.
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <LoadingButton
              loading={isGeneratingReport}
              disabled={isGeneratingReport || !brandId}
              onClick={() => {
                if (!brandId) return;
                generateReport({ brandId });
              }}
            >
              <FileText className="mr-2 h-4 w-4" />
              Generate Report
            </LoadingButton>
          </EmptyContent>
        </Empty>
      )}
    </DashboardPageShell>
  );
}
